import React from "react";
import { Check, Plus } from "lucide-react";

/**
 * ChipGroup — multi-select pills + optional "Otro" free-text input.
 *
 * Props:
 *  - label: string
 *  - hint?: string
 *  - options: string[]
 *  - value: { selected: string[], otro: string }
 *  - onChange: (next) => void
 *  - allowOther?: boolean (default true)
 *  - max?: number (optional cap)
 *  - testId?: string base for data-testid
 *  - intent?: "default" | "danger"
 */
export default function ChipGroup({
    label,
    hint,
    options,
    value,
    onChange,
    allowOther = true,
    max,
    testId,
    intent = "default",
}) {
    const safe = value || { selected: [], otro: "" };
    const selected = safe.selected || [];

    function toggle(opt) {
        const idx = selected.indexOf(opt);
        let next;
        if (idx >= 0) {
            next = selected.filter((s) => s !== opt);
        } else {
            if (max && selected.length >= max) return;
            next = [...selected, opt];
        }
        onChange({ ...safe, selected: next });
    }

    function updOtro(v) {
        onChange({ ...safe, otro: v });
    }

    const danger = intent === "danger";

    return (
        <div data-testid={testId ? `chipgroup-${testId}` : undefined}>
            {label && (
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] mb-3">
                    {label}
                    {hint && (
                        <span className="ml-2 normal-case tracking-normal text-[var(--ink-muted)] text-[11px]">
                            {hint}
                        </span>
                    )}
                </p>
            )}
            <div className="flex flex-wrap gap-2">
                {options.map((opt) => {
                    const active = selected.includes(opt);
                    return (
                        <button
                            type="button"
                            key={opt}
                            onClick={() => toggle(opt)}
                            className={`group inline-flex items-center gap-2 px-3 py-2 border font-mono text-[12px] transition-all ${
                                active
                                    ? danger
                                        ? "bg-[var(--danger)] text-[var(--paper-2)] border-[var(--danger)] shadow-[3px_3px_0_#0a0a0a]"
                                        : "bg-[var(--paper-2)] text-black border-[var(--paper-2)] shadow-[3px_3px_0_var(--danger)]"
                                    : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--danger)] hover:text-[var(--danger)]"
                            }`}
                            data-testid={
                                testId
                                    ? `chip-${testId}-${opt
                                          .toLowerCase()
                                          .replace(/[^a-z0-9]+/g, "-")
                                          .slice(0, 30)}`
                                    : undefined
                            }
                            aria-pressed={active}
                        >
                            {active && <Check className="w-3 h-3" />}
                            <span>{opt}</span>
                        </button>
                    );
                })}
            </div>

            {allowOther && (
                <div className="mt-4">
                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] flex items-center gap-2 mb-2">
                        <Plus className="w-3 h-3" />
                        Otro · escribir con mis palabras
                    </label>
                    <input
                        type="text"
                        value={safe.otro || ""}
                        onChange={(e) => updOtro(e.target.value)}
                        className="input-dossier"
                        placeholder="Algo que no esté en la lista…"
                        data-testid={testId ? `otro-${testId}` : undefined}
                    />
                </div>
            )}

            {(selected.length > 0 || safe.otro) && (
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--warning)] mt-3">
                    Seleccionados: {selected.length + (safe.otro ? 1 : 0)}
                </p>
            )}
        </div>
    );
}
